import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { CreditCard, Building2, Hash, ShieldCheck, Clock, Receipt } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { gorunurAralik } from '../utils/gorunurAralik';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * 💳 KREDİ KARTI ÖDEME SONUCU — panelin üstünden açılan kart
 *
 * Kullanıcı isteği (2026-09-30): kredi kartıyla çekim yapan kişi (özellikle
 * müşteri) panelde gezinirken sonucu kaçırmasın. Firma, tutar, kart, işlem
 * detayı ve başarılı / başarısız bilgisi üstten iner; "Gördüm" denene kadar
 * kalır. Birden çok sonuç varsa sırayla iner.
 *
 * Kaynak: GET /panel/odeme-bildirimleri (yalnız kişinin KENDİ başlattığı
 * ödemeler — sunucu token'dan eşler). Sonuç sayfasının (/odeme-sonuc) üstünde
 * açılmaz; o sayfa zaten dekontu gösteriyor.
 */

const GIZLI_YOLLAR = ['/login', '/odeme-sonuc', '/panel/odeme-sonuc', '/aktivasyon', '/wms/giris'];
const NORMAL_ARALIK = 60000;      // arka planda dakikada bir
const HIZLI_ARALIK = 6000;        // 3D ekranında bekleyen ödeme varken

const TL = (n) => (Number(n) || 0).toLocaleString('tr-TR',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TARIH = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('tr-TR', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' });
};

const TEMA = {
  basarili: { bant: 'bg-[#046A5D]', halka: 'ring-[#046A5D]/15', vurgu: 'text-[#046A5D]', ikon: 'basari' },
  basarisiz: { bant: 'bg-[#C41D14]', halka: 'ring-[#C41D14]/15', vurgu: 'text-[#C41D14]', ikon: 'hata' },
  dogrulama_bekliyor: { bant: 'bg-[#B45309]', halka: 'ring-[#B45309]/15', vurgu: 'text-[#B45309]', ikon: 'bekle' },
};

const hareketAzalt = () =>
  typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Durum ikonu — çizilerek gelen tik / çarpı, bekleyen için nabız. */
function DurumIkonu({ tur }) {
  if (tur === 'bekle') {
    return (
      <span className="odeme-ikon-nabiz inline-flex w-11 h-11 rounded-full bg-white/15 items-center justify-center">
        <Clock size={24} className="text-white" />
      </span>
    );
  }
  return (
    <svg viewBox="0 0 44 44" width="44" height="44" aria-hidden="true" className="shrink-0">
      <circle cx="22" cy="22" r="20" fill="rgba(255,255,255,0.16)" className="odeme-ikon-halka" />
      {tur === 'basari' ? (
        <path d="M13 22.5l6 6 12-13" fill="none" stroke="#fff" strokeWidth="3.4"
          strokeLinecap="round" strokeLinejoin="round" className="odeme-ikon-ciz" />
      ) : (
        <>
          <path d="M15 15l14 14" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round"
            className="odeme-ikon-ciz" />
          <path d="M29 15L15 29" fill="none" stroke="#fff" strokeWidth="3.4" strokeLinecap="round"
            className="odeme-ikon-ciz odeme-ikon-ciz-2" />
        </>
      )}
    </svg>
  );
}

function Satir({ ikon: Ikon, etiket, children }) {
  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-slate-100 last:border-0">
      <Ikon size={15} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1 flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="text-[11px] font-semibold tracking-wide text-slate-500">{etiket}</span>
        <span className="text-[13px] text-slate-800 text-right break-words min-w-0">{children}</span>
      </div>
    </div>
  );
}

export default function OdemeSonucBildirimi() {
  const { user, token } = useAuth();
  const location = useLocation();
  const [kuyruk, setKuyruk] = useState([]);
  const [cikiyor, setCikiyor] = useState(false);
  const [beklemedeVar, setBeklemedeVar] = useState(false);
  const kutlananRef = useRef(new Set());
  const kartRef = useRef(null);
  const gorulenRef = useRef(new Set());   // "Gördüm" denip sunucuya giden — tekrar inmesin

  const gizli = !user || !token
    || GIZLI_YOLLAR.some((y) => location.pathname === y || location.pathname.startsWith(y + '/'));

  // Yalnız MES'e giren hesap ödeme başlatamaz — yoklama yok (kullanıcı belli olana kadar da beklenir)
  const yalnizMes = !user || user.kullanici_tipi === 'mes';
  const yukle = useCallback(async () => {
    if (!token || yalnizMes) return;
    try {
      const { data } = await axios.get(`${API}/panel/odeme-bildirimleri`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!data?.success) return;
      setBeklemedeVar(!!data.beklemede_var);
      const yeni = (data.kayitlar || []).filter((k) => !gorulenRef.current.has(k.id));
      setKuyruk((onceki) => {
        // Ekrandaki kart yerinde kalsın (yeniden inmesin); yeni gelenler arkaya
        const ids = new Set(onceki.map((k) => k.id));
        const ekle = yeni.filter((k) => !ids.has(k.id));
        const kalan = onceki.filter((k) => yeni.some((y) => y.id === k.id));
        return ekle.length || kalan.length !== onceki.length ? [...kalan, ...ekle] : onceki;
      });
    } catch {
      /* ağ hatası: sessiz — bir sonraki yoklamada tekrar denenir */
    }
  }, [token, yalnizMes]);

  // Giriş, sayfa değişimi, sekmeye dönüş — 10 sn içinde tekrar sorulmaz: her sayfa geçişinde ve sekmeye
  // dönüşte (focus + visibilitychange + gorunurAralik) art arda 3 istek gidiyordu (2026-10-23).
  // Bekleyen 3D ödeme varken sınır yok (sonuç hemen görünsün).
  const sonYuklemeRef = useRef(0);
  const yukleSeyrek = useCallback(() => {
    if (!beklemedeVar && Date.now() - sonYuklemeRef.current < 10000) return;
    sonYuklemeRef.current = Date.now();
    yukle();
  }, [yukle, beklemedeVar]);
  useEffect(() => { if (token) yukleSeyrek(); }, [token, location.pathname, yukleSeyrek]);
  useEffect(() => {
    const odak = () => { if (document.visibilityState === 'visible') yukleSeyrek(); };
    window.addEventListener('focus', odak);
    document.addEventListener('visibilitychange', odak);
    return () => {
      window.removeEventListener('focus', odak);
      document.removeEventListener('visibilitychange', odak);
    };
  }, [yukleSeyrek]);
  // Yoklama: 3D ekranında bekleyen ödeme varsa sık, yoksa dakikada bir
  useEffect(() => {
    if (!token) return undefined;
    // Bekleyen 3D ödeme varken arka planda da sorulur; yoksa yalnız görünür sekmede
    return gorunurAralik(yukleSeyrek, beklemedeVar ? HIZLI_ARALIK : NORMAL_ARALIK, beklemedeVar ? HIZLI_ARALIK : null);
  }, [token, beklemedeVar, yukleSeyrek]);

  const aktif = kuyruk[0];

  // Başarılı ödemede kartın altından kısa bir konfeti (bir kez, hareket azaltılmadıysa)
  useEffect(() => {
    if (gizli || !aktif || aktif.durum !== 'basarili' || kutlananRef.current.has(aktif.id)) return;
    kutlananRef.current.add(aktif.id);
    if (hareketAzalt()) return;
    const t = setTimeout(() => {
      const r = kartRef.current?.getBoundingClientRect();
      const y = r ? Math.min(0.6, (r.bottom - 40) / window.innerHeight) : 0.25;
      confetti({ particleCount: 70, spread: 75, startVelocity: 32, ticks: 160, scalar: 0.85,
        origin: { x: 0.5, y }, colors: ['#046A5D', '#0E3A46', '#FFE7B8', '#ffffff'], zIndex: 100002 });
    }, 520);
    return () => clearTimeout(t);
  }, [aktif, gizli]);

  const kapat = async (hepsi = false) => {
    if (!aktif || cikiyor) return;
    const giden = hepsi ? kuyruk.map((k) => k.id) : [aktif.id];
    giden.forEach((id) => gorulenRef.current.add(id));
    setCikiyor(true);
    try {
      await axios.post(hepsi
        ? `${API}/panel/odeme-bildirimleri/goruldu-hepsi`
        : `${API}/panel/odeme-bildirimleri/${encodeURIComponent(aktif.id)}/goruldu`,
      {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      /* işaretlenemese de kullanıcı gördü — bu oturumda tekrar inmez */
    }
    setTimeout(() => {
      setKuyruk((k) => k.filter((x) => !giden.includes(x.id)));
      setCikiyor(false);
    }, hareketAzalt() ? 0 : 320);
  };

  if (gizli || !aktif) return null;
  const tema = TEMA[aktif.durum] || TEMA.dogrulama_bekliyor;
  const kart = aktif.kart || {};
  const kartMetni = [kart.banka, kart.son4 ? `•••• ${kart.son4}` : '', kart.tip]
    .filter(Boolean).join(' · ');

  return (
    <>
      <div className={`fixed inset-0 z-[100000] bg-slate-900/30 backdrop-blur-[2px]
                       ${cikiyor ? 'odeme-perde-cik' : 'odeme-perde-gir'}`} aria-hidden="true" />
      <div className="fixed inset-x-0 top-0 z-[100001] flex justify-center px-4 pt-3 pointer-events-none">
        <section
          key={aktif.id}
          ref={kartRef}
          role="alertdialog" aria-modal="true" aria-labelledby="odeme-bildirim-baslik"
          className={`pointer-events-auto w-full max-w-[540px] bg-white rounded-2xl shadow-2xl
                      ring-8 ${tema.halka} overflow-hidden
                      ${cikiyor ? 'odeme-kart-cik' : 'odeme-kart-in'}
                      ${!cikiyor && aktif.durum === 'basarisiz' ? 'odeme-kart-salla' : ''}`}>
          {/* Durum bandı */}
          <div className={`${tema.bant} text-white px-5 py-4 flex items-center gap-3.5`}>
            <DurumIkonu tur={tema.ikon} />
            <div className="min-w-0 flex-1">
              <h2 id="odeme-bildirim-baslik" className="text-[17px] font-bold leading-tight">
                {aktif.durum_ad}
              </h2>
              <p className="text-[12px] text-white/80 mt-0.5 truncate">
                {aktif.islem_tipi}{aktif.tarih ? ` · ${TARIH(aktif.tarih)}` : ''}
              </p>
            </div>
            {kuyruk.length > 1 && (
              <span className="shrink-0 text-[11px] font-semibold bg-white/15 rounded-full px-2 py-0.5 tabular-nums">
                1 / {kuyruk.length}
              </span>
            )}
          </div>

          <div className="px-5 pt-4 pb-3">
            {/* Tutar — kartın en büyük nesnesi */}
            <div className="flex items-end justify-between gap-3">
              <div>
                <div className="text-[11px] font-semibold tracking-wide text-slate-500">Tutar</div>
                <div className={`text-[30px] leading-none font-bold tabular-nums mt-1
                                 ${aktif.durum === 'basarisiz' ? 'text-slate-400 line-through decoration-2' : 'text-slate-900'}`}>
                  {TL(aktif.tutar)} <span className="text-[18px] font-semibold">₺</span>
                </div>
              </div>
              <span className={`text-[12px] font-semibold ${tema.vurgu} bg-slate-50 border border-slate-200
                                rounded-lg px-2 py-1 shrink-0`}>
                {aktif.taksit > 1 ? `${aktif.taksit} taksit` : 'Tek çekim'}
              </span>
            </div>

            {aktif.durum === 'basarisiz' && aktif.hata && (
              <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5">
                <p className="text-[13px] font-semibold text-red-800">{aktif.hata.mesaj}</p>
                {aktif.hata.oneri && (
                  <p className="text-[12px] text-red-700 mt-1 leading-snug">{aktif.hata.oneri}</p>
                )}
                <p className="text-[11px] text-red-600/80 mt-1.5">Bu işlemde karttan çekim yapılmadı.</p>
              </div>
            )}
            {aktif.durum === 'dogrulama_bekliyor' && (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5
                              text-[12px] text-amber-900 leading-snug">
                Banka onayı henüz doğrulanamadı; ödeme kontrol ediliyor. Kesinleşince
                cari hesaba işlenir. Tekrar çekim denemeden önce durumu teyit edin.
              </div>
            )}

            <div className="mt-3">
              <Satir ikon={Building2} etiket="Firma">{aktif.firma_adi}</Satir>
              {aktif.cari_unvani && (
                <Satir ikon={Receipt} etiket="Cari">
                  {aktif.cari_unvani}
                  {aktif.cari_kodu && <span className="text-slate-400"> · {aktif.cari_kodu}</span>}
                </Satir>
              )}
              {kartMetni && <Satir ikon={CreditCard} etiket="Kart">{kartMetni}</Satir>}
              {aktif.onay_kodu && <Satir ikon={ShieldCheck} etiket="Onay kodu">
                <span className="font-mono tabular-nums">{aktif.onay_kodu}</span></Satir>}
              <Satir ikon={Hash} etiket="İşlem no">
                <span className="font-mono text-[12px] break-all">{aktif.islem_no}</span>
                {aktif.siparis_no && <span className="block text-[11px] text-slate-500">Sipariş {aktif.siparis_no}</span>}
              </Satir>
            </div>
          </div>

          <div className="px-5 pb-5 pt-1 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
            {kuyruk.length > 1 && (
              <button onClick={() => kapat(true)} disabled={cikiyor}
                className="px-4 py-2.5 rounded-xl text-[13px] font-semibold text-slate-600 border border-slate-200
                           hover:bg-slate-50 disabled:opacity-50 focus:outline-none focus-visible:ring-2
                           focus-visible:ring-slate-400">
                Hepsini gördüm ({kuyruk.length})
              </button>
            )}
            <button onClick={() => kapat(false)} disabled={cikiyor} autoFocus
              className="px-6 py-2.5 rounded-xl text-[14px] font-bold text-white bg-slate-900 hover:bg-slate-800
                         disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
                         focus-visible:ring-slate-900">
              Gördüm
            </button>
          </div>
        </section>
      </div>
    </>
  );
}
